import React, { Component } from "react";

import { defined } from "@formant/core/common/defined";
import { IDevice } from "@formant/core/model/IDevice";
import { IconName } from "@formant/ui/component/common/Icon";

import { track } from "../../../analytics";
import { appStore, authStore, deviceStore, routerStore } from "../../../stores";
import { Buttons } from "../../common/Buttons";
import { Help } from "../../common/Help";
import {
    IMultiActionButtonAction,
    MultiActionButton,
} from "../../Observe/MultiActionButton";
import * as styles from "./Actions.scss";

export interface IActionsProps {
    device: IDevice;
    onReprovision: (device: IDevice) => void;
}

export class Actions extends Component<IActionsProps> {
    public render() {
        const { onView, onSaveAsDeviceConfigurationTemplate, onDelete } = this;
        const reprovisionDevice = authStore.hasAccess(
            "devices",
            "administrator",
        )
            ? {
                  icon: "tools" as IconName,
                  label: "Reprovision",
                  onSelect: this.onReprovision,
                  tooltip:
                      "Reprovisioning a device ensures that a new agent assumes the identity of the previous agent hence the data will be from the same device.",
              }
            : undefined;
        const actions: IMultiActionButtonAction[] = [
            {
                icon: "device",
                label: "View",
                onSelect: onView,
            },
            {
                icon: "configuration",
                label: "Save as configuration template",
                onSelect: onSaveAsDeviceConfigurationTemplate,
            },
            ...(reprovisionDevice ? [reprovisionDevice] : []),
            {
                icon: "delete",
                label: "Delete device",
                onSelect: onDelete,
            },
        ];

        return (
            <Buttons>
                <MultiActionButton actions={actions} />
            </Buttons>
        );
    }

    private onView = async () => {
        const { navigate, routeTo } = routerStore;
        await navigate(routeTo.device({ id: defined(this.props.device.id) }));
    };

    private onSaveAsDeviceConfigurationTemplate = () => {
        const { device } = this.props;
        const { name, id } = device;
        const { navigate, routeTo } = routerStore;
        appStore.setConfirm({
            title: "Save device configuration",
            message: (
                <>
                    <p>
                        Saving this device configuration as a template allows
                        the configuration to be used by other devices in your
                        fleet.
                    </p>
                    <p>Save configuration for device "{name}"?</p>
                </>
            ),
            onOk: () =>
                navigate(
                    routeTo.exportDeviceConfigurationTemplate({
                        id: defined(id),
                    }),
                ),
        });
    };

    private onDelete = () => {
        track("Device deletion started");
        const { device } = this.props;
        const { name, id } = device;
        const { routeTo } = routerStore;
        const message = (
            <div className={styles["delete-message"]}>
                <p>
                    Are you sure you want to delete
                    {id ? (
                        <a
                            className={styles.name}
                            target="blank"
                            href={routeTo.device({ id })}
                            data-tooltip="Open the device page in a new tab"
                        >
                            {` ${name}`}
                        </a>
                    ) : (
                        <span className={styles.name}>{` ${name}`}</span>
                    )}
                    ?
                </p>
                <p>
                    Deleting a device will remove it from the organization.
                    <br />
                    This action is irreversible.
                </p>
            </div>
        );
        const titleHelp = (
            <Help
                text="Formant Docs: Delete, disable or uninstall agent"
                to="https://docs.formant.io/docs/managing-a-native-installation#uninstalling-the-formant-agent"
            />
        );
        appStore.setConfirm({
            title: "Delete device",
            message,
            okCopy: "Delete",
            titleHelp,
            onOk: async () => {
                track("Device deleted");
                await deviceStore.disable(defined(id));
                appStore.setSnackbar("Device deleted");
            },
        });
    };

    private onReprovision = () => {
        track("Reprovision started");
        const { device, onReprovision } = this.props;
        const { name } = device;
        const titleHelp = (
            <Help
                text="Replace agent security token for this device"
                to="https://docs.formant.io/docs/re-provisioning-an-agent"
            />
        );
        appStore.setConfirm({
            title: "Reprovision device",
            message: `The current provisioning token will expire and the device will be inactive until you apply the token obtained in the next step. You cannot undo this action. Are you sure you want to reprovision the device "${name}"?`,
            titleHelp,
            onOk: () => {
                track("Reprovision completed");
                onReprovision(device);
            },
        });
    };
}
